import { createDebug } from '@/utils/debug'
import { withTimeout } from '@/utils/promise'
import {
  isDesktopOfficeHost,
  readSelectedSlideId,
  readSelectedSlideIdBounded,
  readDocumentSetting,
} from '@/services/office/helpers'
import { canReadSlideTags, readTagsAtSlideOnce, healTagsAtSlideIfUnchanged } from '@/services/office/slide-tags'
import { matchesPreviousSlideId } from '@/services/office/slide-binding-history'
import { loadPptCommonSlideBinding } from '@/services/office/ppt-online-binding'
import { readShowCurrentSlideId } from '@/services/office/ppt-online-current-slide'
import {
  publishShowOwnerClaim,
  loadShowOwnerClaim,
} from '@/services/office/ppt-show-owner-claim'

// AHA-46751: "which slide is PowerPoint showing, and is it mine?" — answered per platform, from
// whichever per-slide store that platform actually has. Desktop reads the host slide's TAGS;
// web reads ppt-online-binding's per-slide localStorage bridge (see THE WEB PATH below). The
// question, the three-way verdict and both callers are shared; only the lookup differs.
//
// Not to be confused with ppt-online-current-slide.js, which answers a different question: a frame
// the Slide Show refuses to serve learns WHICH SLIDE IS UP from another frame over localStorage.
// That is the input to this module's web arm, not a rival to it.
//
// THE DEFECT THIS EXISTS FOR. Every Office signal behind auto-start is document-global:
// getActiveViewAsync and getSelectedDataAsync answer for the DOCUMENT, not for the add-in frame
// asking. So in a deck holding several AhaSlides slides, entering Slide Show wakes every frame at
// once, each sees the same shown slide, and each concludes it is the one on screen — the audience
// then oscillates between whichever slides won the last race. services/office/index.js used to
// resolve this by having each frame capture the shown slide id on entering read view and treat it
// as its own (savedHostPptSlideId); that capture is the false claim, not a fix for it.
//
// The host slide TAG is the only per-slide identity in the document, so this module compares the
// tag on the shown slide against the frame's own dataSlidePPT binding. No capture, no id to keep.
//
// Toggle logging with: localStorage.debug = 'aha:ppt:auto-start' — the same namespace as the
// auto-start flow in services/office/index.js on purpose, so a start decision and the ownership
// check behind it read as one trace instead of two that have to be interleaved by hand.
const log = createDebug('ppt:auto-start')

// Matches the legacy poll's cadence (services/office/index.js). The tick itself is only the cheap
// Common-API selection read; see pollSelectedSlideBasedOnTag for what makes that affordable.
const SLIDE_POLL_INTERVAL_MS = 250

// Every ownership verdict names the shown slide by its Common-API id: it is the one identifier
// that appears identically in every frame's trace, so it is what lets several frames' logs be
// lined up against each other rather than read one frame at a time.
const SHOWN_ID_LABEL = '| shown ppt slide id ='

// THREE outcomes, not two — 'unknown' is load-bearing. The two callers want opposite things from
// an unanswerable question: entering read view should still start (better a possible duplicate
// than a presenter staring at a dead slide), while the focus poll must do nothing (the frame has
// already started; guessing there would resurrect the very oscillation this fixes). Collapsing
// them into a boolean would force one of the two to be wrong.
export const SLIDE_OWNERSHIP = { MINE: 'mine', OTHER: 'other', UNKNOWN: 'unknown' }

// The confirm read gets its own, tighter budget than the cold-load tag reads in insert-slides
// (BINDING_READ_TIMEOUT_MS x 3 attempts). That budget is sized for "we have nowhere else to be";
// this one sits between the presenter starting the show and the audience seeing the slide, where
// every millisecond is visible. One attempt only — the caller fails OPEN on a null, so a slow
// host costs a late suppression check, never a missing start.
const ACTIVE_SLIDE_BINDING_TIMEOUT_MS = 1200

let activeSlideConfirmInFlight = null
// AHA-43274: the Common-API id of THIS FRAME'S OWN host slide, learned the only way it can be —
// off a tag read that already answered MINE — and then reused so the frame never pays for that read
// twice.
//
// The tag is per-slide but addressed by INDEX (readTagsAtSlideOnce takes the 1-based index), while
// getSelectedDataAsync answers with an ID. That mismatch is the whole reason readActiveSlideBinding
// re-reads the selection: it needs the index the tick's own read threw away. Caching the id removes
// the detour for the case that dominates a show — the shown slide is still this frame's — leaving a
// string compare where there was a second selection read AND a PowerPoint.run batch.
//
// Why that is more than a saving. NO_OWNER_STOP_DELAY_MS (1000ms) is deliberately shorter than the
// worst-case confirm its own comment computes (1450ms), so a show returning to this frame's slide
// could not reliably cancel an armed stop before it fired — the quiz ended under a presenter who had
// come back. On the fast path the MINE verdict lands inside the tick that read the selection, which
// is within the debounce by construction rather than by luck.
//
// STRICTLY ONE-WAY, and that is the property to preserve. It can only ever short-circuit TO MINE;
// every other id falls through to the full confirm. So it cannot manufacture the not-MINE verdict
// that ends a quiz — only cancel one. The two failure directions here are unequal (see
// NO_OWNER_STOP_DELAY_MS), and this is the only direction a cache is allowed to be wrong in.
//
// Recorded only on a CLEAN raw-tag match, never on one that needed the binding history to get there:
// that case fires healStaleTagBinding, and short-circuiting it would retire the re-read its retry
// depends on. Once the heal lands the raw tag matches and the fast path opens on its own.
//
// Dropped with the rest of the per-show state in stopTagSlideFocusPolling. The id is stable for the
// life of a frame, but the deck is editable between shows, and re-learning costs one tag read on the
// first tick of the next one.
let ownCommonSlideId = null
// AHA-46375: two PowerPoint.run batches in one frame deadlock each other's context.sync(). The
// repair is fire-and-forget from its caller's point of view, so it can still be in flight when
// the poll asks for the next verdict — the next confirm waits on it rather than racing it.
let healInFlight = null
let tagSlidePollTimer = null
let tagPollResolvedSlideId = null
let tagPollBusy = false
// AHA-43274: the MINE -> not-MINE EDGE, which is what licenses this frame to end the quiz. Level
// would not do: several frames can be "not the owner" at once, and every one of them would then
// dispatch a stop. Only the frame that was PRESENTING has anything to end, and there is at most one
// of those, so keying on the transition makes the dispatch single-sourced by construction rather
// than by a cross-frame election localStorage has no CAS to run.
//
// CONSUMED on the transition, deliberately. Left sticky, a frame that handed over earlier in the
// show would still be "previously active" on every later navigation and would arm alongside the
// real owner — the duplicate dispatch this exists to avoid.
let wasMineLastVerdict = false
let pendingStopTimer = null

// One getSelectedDataAsync round-trip, keeping BOTH halves of its answer: the 1-based index the
// Rich API tag batch needs, and the Common-API slide id that names the slide in the trace.
const readActiveSlideSelection = () => new Promise((resolve, reject) => {
  readSelectedSlideId((selectedId, error, slides) => {
    const index = slides && slides[0] && slides[0].index
    if (error || !index) {
      reject(new Error(error || 'Could not identify the shown PowerPoint slide'))
      return
    }
    resolve({ index, selectedId })
  })
})

// AHA-46751: read the binding tags off the slide PowerPoint is CURRENTLY SHOWING — as opposed to
// insert-slides' readSlideBindingFromHostSlide, which answers "the slide THIS frame lives on".
//
// The distinction is the whole point, and it is why this read lives here rather than beside that
// one. getSelectedDataAsync is document-global, which makes it the WRONG answer to "which slide am
// I on?" (the AHA-43111 defect) but the RIGHT answer to "which slide is on screen?" — in the Slide
// Show it tracks the live position, back-navigation included. Auto-start needs the second
// question: every add-in frame in the deck enters read view at the same moment, and only the one
// whose binding matches the shown slide should start.
//
// Both halves are bounded TOGETHER: readActiveSlideSelection has no timeout of its own, so
// bounding only the tag batch would leave a hung selection read stalling the auto-start
// indefinitely — turning a fail-open check into a silent fail-closed one.
//
// A `binding` of null means "cannot confirm" (no Rich API, pre-1.3 host, untagged slide, timed
// out), never "does not match". The caller decides what to do with that; today it starts anyway.
//
// `commonSlideId` is the getSelectedDataAsync id of the slide we looked at, carried out purely so
// the trace can NAME the slide every verdict is about. Reported separately from the binding, and
// on the failure paths too, because the cases worth debugging are exactly the ones where the
// binding is null: "no readable tag" is unactionable, "no readable tag on slide 259" can be
// checked against the deck.
//
// `readable` says whether the tag batch ANSWERED, which is a different question from whether it
// found a tag. A null binding with readable=true is the determinate answer "this slide carries no
// AhaSlides tag" — the dominant case in a real deck, since queueBindingTagRead returns null on
// isNullObject for every ordinary PowerPoint slide. A null binding with readable=false is "could
// not tell" (no Rich API, pre-1.3 host, rejected or timed-out batch). Only the poll cares, and it
// cares a lot: the first will not change mid-show and is safe to memoise, the second must be
// re-asked or a transient failure writes a slide off for the rest of the show.
const readActiveSlideBinding = async () => {
  if (!canReadSlideTags()) {
    return { commonSlideId: null, binding: null, readable: false }
  }
  // Deliberately not insert-slides' readHostSlideIndex, which resolves the index alone: this
  // module wants the id that one discards. Same Office call, one extra field kept.
  let commonSlideId = null
  let hostSlideIndex = null
  const run = (async () => {
    const { index, selectedId } = await readActiveSlideSelection()
    // Recorded as soon as they are known rather than returned, so the timeout branch below can
    // still name the slide whose tag read was the thing that stalled. The index is kept for the
    // stale-tag repair, which must write to the slide this verdict was actually about.
    commonSlideId = selectedId
    hostSlideIndex = index
    return readTagsAtSlideOnce(index)
  })()
  // An abandoned batch settles later with nobody listening; swallow so it cannot surface as an
  // unhandled rejection.
  run.catch(() => {})
  try {
    const binding = await withTimeout(
      run,
      ACTIVE_SLIDE_BINDING_TIMEOUT_MS,
      'active slide tag read timed out',
    )
    log('active slide binding =', binding, '| ppt slide id =', commonSlideId)
    // The batch answered: a null `binding` here means "this slide carries no tag", not
    // "could not tell" — and it will not grow one mid-show.
    return {
      commonSlideId, hostSlideIndex, binding, readable: true,
    }
  } catch (error) {
    log('read active slide binding failed | ppt slide id =', commonSlideId, error)
    return {
      commonSlideId, hostSlideIndex, binding: null, readable: false,
    }
  }
}

// Repairs a host-slide tag that a present-run reset left holding a pre-remap slide id, AFTER the
// ownership check has confirmed the slide is this frame's. Deliberately not awaited by the
// verdict: the presenter is waiting on that verdict, and a tag rewrite is bookkeeping. Never
// rejects, so the serialisation await in resolveActiveSlideOwnership needs no catch of its own.
const healStaleTagBinding = (hostSlideIndex, expected, next) => {
  healInFlight = healTagsAtSlideIfUnchanged(hostSlideIndex, expected, next)
    .then((healed) => {
      log('heal stale tag at index', hostSlideIndex, '->', healed ? 'written' : 'slide changed, skipped')
      return healed
    })
    .catch((error) => {
      // Best-effort. The binding's own history still carries the comparison, so a failed repair
      // costs only that: the divergence is carried rather than ended, until some later write
      // replaces the binding and takes the history with it.
      log('heal stale tag failed', error)
      return false
    })
    .finally(() => { healInFlight = null })
  return healInFlight
}

// ---------------------------------------------------------------------------------------------
// THE WEB PATH
//
// The desktop check below rests on the host slide's TAG, which the web Slide Show cannot read at
// all: it is a brand-new session where PowerPoint.run throws RichApi.Error. But the web already
// keeps a store that answers the same question — ppt-online-binding's per-slide bridge, a
// localStorage map from the Common-API slide id to { presentationId, slideId }, written in the
// edit session (insert-slides on every insert, openSlideFromHostSlideTag on every cold load) and
// keyed by document url so it cannot leak across decks. Its own comment records the probe that
// makes it usable here: the Common-API id of a given slide is IDENTICAL in the edit session where
// we write and the Slide Show session where we read.
//
// So the web comparison is the same shape as the desktop one with the map standing in for the tag:
//
//   shown  = loadPptCommonSlideBinding(<id getSelectedDataAsync reports>)   // whose slide is up
//   mine   = store.state.powerPointPresentationId / powerPointSlideId       // whose frame am I
//
// BOTH SIDES ARE POST-REMAP, which is what makes the comparison safe across a present-run reset —
// the divergence AHA-46751 exists for. The store side is remapped by definition
// (setDataSlideInPowerpointToState is only ever called with the output of
// handleGetCorrentSlideIdByMultipleSession). The bridge side is raw as WRITTEN, in the edit
// session, but routeToCommonSlide refreshes the entry with the remapped pair as it resolves this
// frame — in the Slide Show session, before the poll starts. That is the web analogue of the
// desktop path's healTagsAtSlideIfUnchanged, and it is why this arm needs no previousSlideIds
// history: the reset is absorbed at cold load rather than carried into the comparison.
//
// WHAT "MINE" IS ON WEB, and why it is neither document.settings nor new state. Settings are
// shared live across frames on the web (office-js #2765), and loadPptOnlineBinding is one
// document-wide value — both give every frame the same answer, which is precisely the thing that
// cannot distinguish frames. The binding this frame RESOLVED, though, is already per-frame and
// already stored: store.state.powerPointPresentationId / powerPointSlideId, written by
// setDataSlideInPowerpointToState on every cold-load branch and read the same way elsewhere
// (PresentationDetailUtils picks the frame's active slide from exactly this pair).
//
// It is injected rather than imported because this module is deliberately a leaf — see the note on
// startTagSlideFocusPolling for why importing @/vuex/store here would close a cycle.
let readOwnBinding = () => null

export const registerOwnBindingReader = (reader) => {
  readOwnBinding = typeof reader === 'function' ? reader : () => null
}

// Exported for tests; production registers once from setupAutoStartViewMachine.
export const clearOwnBindingReader = () => { readOwnBinding = () => null }

// The shown slide's Common-API id, for the callers that do not already have one. Bounded, because
// in the web Slide Show getSelectedDataAsync answers ONE frame per document and leaves the rest
// outstanding forever (ppt-online-current-slide) — an unbounded await would hang the check in
// exactly the frames that most need an answer. A starved frame falls back to the value the served
// frame broadcast, which is the same channel the cold load already resolves those frames from.
const WEB_SHOWN_SLIDE_READ_TIMEOUT_MS = 1200

const readWebShownSlideId = async () => {
  try {
    const selectedId = await readSelectedSlideIdBounded(WEB_SHOWN_SLIDE_READ_TIMEOUT_MS)
    if (selectedId) return selectedId
  } catch (error) {
    log('web: own selection read unavailable |', error && error.message)
  }
  return readShowCurrentSlideId()
}

// Resolves the same { ownership, commonSlideId, readable } contract as the desktop path.
//
// `readable` splits exactly as it does on desktop, and for the same reason. A map MISS is a
// SETTLED answer about that slide — nothing writes the bridge during a Slide Show, so the entry
// will not appear mid-show — and is memoised, which is what stops an untagged slide re-asking on
// every 250ms tick. Not being able to identify the shown slide, or not yet having a binding of our
// own, is frame-scoped rather than slide-scoped: the cold-load registration can land after the
// poll has started, so memoising it would write a slide off over a fact that had nothing to do
// with that slide.
const resolveWebActiveSlideOwnership = async (knownCommonSlideId) => {
  const resolved = readOwnBinding()
  const own = (resolved && resolved.presentationId && resolved.slideId) ? resolved : null
  const commonSlideId = knownCommonSlideId || await readWebShownSlideId()
  if (!commonSlideId || !own) {
    log('web ownership: cannot compare -> unknown', SHOWN_ID_LABEL, commonSlideId || null,
      '| mine =', own)
    return { ownership: SLIDE_OWNERSHIP.UNKNOWN, commonSlideId: commonSlideId || null, readable: false }
  }
  const shown = loadPptCommonSlideBinding(commonSlideId)
  if (!shown || !shown.presentationId || !shown.slideId) {
    log('web ownership: shown slide is not in the bridge -> unknown', SHOWN_ID_LABEL,
      commonSlideId)
    return { ownership: SLIDE_OWNERSHIP.UNKNOWN, commonSlideId, readable: true }
  }
  // Strings on both sides for the same reason the desktop arm does it: the bridge round-trips
  // through JSON while the registered binding may hold numbers, and a shape mismatch would read as
  // "not mine" and suppress a legitimate start.
  const isMine = String(shown.presentationId) === String(own.presentationId)
    && String(shown.slideId) === String(own.slideId)
  log('web ownership: shown =', shown, SHOWN_ID_LABEL, commonSlideId,
    '| mine =', own, '| isMine =', isMine)
  return {
    ownership: isMine ? SLIDE_OWNERSHIP.MINE : SLIDE_OWNERSHIP.OTHER,
    commonSlideId,
    readable: true,
  }
}

// The desktop arm's body, lifted out of the single-flight wrapper above so that wrapper stays the
// two-line platform switch it now is.
async function runDesktopOwnershipConfirm() {
  // Never overlap the repair's batch with this one (AHA-46375). It never rejects.
  if (healInFlight) await healInFlight
  // Issued together, not in sequence. The settings read is a Common API (refreshAsync) and the
  // tag read a Rich API batch, so they do not contend the way two PowerPoint.run batches do
  // (AHA-46375) — the check costs max(settings, tags) rather than the sum, on a path the
  // presenter is watching. Sequencing them would let an unbound frame skip the tag read, but
  // auto-start barely reaches that case (autoStartActiveSlideFromView already requires a bound
  // presentation and slide), so it would buy a rare saving with latency on every real start.
  const [ownBinding, shown] = await Promise.all([
    readDocumentSetting('dataSlidePPT'),
    readActiveSlideBinding(),
  ])
  // Every branch reports the shown slide's PowerPoint id. It is the one identifier that is the
  // same across all the add-in frames' traces, so it is what lets several frames' logs be lined
  // up against each other — which is the only way to see the oscillation itself rather than one
  // frame's share of it.
  const {
    commonSlideId, hostSlideIndex, binding: activeBinding, readable,
  } = shown
  if (!ownBinding || !ownBinding.presentationId || !ownBinding.slideId) {
    // DELIBERATELY readable=false, even though the tag batch may well have answered. This
    // UNKNOWN is about THIS FRAME, not about the shown slide, so the poll's per-slide memo is
    // the wrong key for it — memoising here would write off a slide over a fact that has
    // nothing to do with that slide. And the fact is not even settled: readDocumentSetting
    // resolves null both for "no binding" and for a FAILED refreshAsync, and the cold-load
    // settings write (handleGetCorrentSlideIdByMultipleSession) can land after the poll has
    // already started on the Slide-Show entry reload. Memoising an early tick would then leave
    // this frame's own slide permanently written off — a dead slide, the exact failure this
    // module exists to stop. An unbound frame therefore keeps re-asking; it is rare (it
    // resolved no binding at all), unlike the untagged-slide case below, which is every
    // ordinary slide in the deck and is what makes the poll affordable.
    log('ownership: this frame has no single-slide binding in settings -> unknown',
      SHOWN_ID_LABEL, commonSlideId)
    return { ownership: SLIDE_OWNERSHIP.UNKNOWN, commonSlideId, readable: false }
  }
  if (!activeBinding) {
    // Settled when the batch answered (`readable`): a plain PowerPoint slide carries no tag and
    // will not grow one mid-show. Not settled when it did not — a timed-out or rejected read is
    // exactly what must be re-asked.
    log('ownership: shown slide has no readable tag -> unknown', SHOWN_ID_LABEL,
      commonSlideId, '| tag read answered =', readable)
    return { ownership: SLIDE_OWNERSHIP.UNKNOWN, commonSlideId, readable }
  }
  // Tags are written with String(); settings hold numbers. Compare as strings so the shapes
  // cannot make a real match look like a mismatch and silently suppress a legitimate start.
  //
  // AHA-46751: the shown tag also counts as this frame's when it names an id this frame's
  // binding used to point at. A server-side reset moves every slide id in the presentation —
  // document.settings are remapped on the next cold load, the tag is not — so without the
  // history arm the frame that genuinely owns the shown slide compares a current id against a
  // stale one and suppresses its own start.
  //
  // The two arms are OR'd, never substituted, which keeps the history strictly ADDITIVE: it can
  // only ever add a match, never remove one. The raw arm is exactly the comparison that shipped
  // before the reset was handled at all, so a wrong or stale history entry can never make this
  // check worse than it was — and suppressing a legitimate start is the expensive failure here,
  // since the presenter gets a dead slide.
  const matchesRawTag = String(activeBinding.slideId) === String(ownBinding.slideId)
  const matchesHistory = matchesPreviousSlideId(ownBinding, activeBinding.slideId)
  const isMine = String(activeBinding.presentationId) === String(ownBinding.presentationId)
      && (matchesRawTag || matchesHistory)
  log('ownership: shown =', activeBinding, SHOWN_ID_LABEL, commonSlideId,
    '| mine =', ownBinding, '| isMine =', isMine,
    '| viaRawTag =', matchesRawTag, '| viaHistory =', matchesHistory)
  // Learn this frame's own host slide id, so every later tick that lands on it can answer without
  // opening a batch. Gated on matchesRawTag rather than isMine: a history-only match is about to be
  // healed below, and caching it would remove the re-read the repair retries on. commonSlideId comes
  // from the same selection read that produced the index this tag was read at, so the pair is
  // self-consistent even when the tick that asked has since moved on.
  if (isMine && matchesRawTag && commonSlideId !== null) {
    ownCommonSlideId = commonSlideId
  }
  // Only a match that NEEDED the history to get there. A tag already naming this frame's slide
  // has nothing to repair — the rewrite would be a no-op — and a slide that is not ours must
  // never be written to at all.
  if (isMine && !matchesRawTag && hostSlideIndex) {
    healStaleTagBinding(hostSlideIndex, activeBinding, {
      presentationId: ownBinding.presentationId,
      slideId: ownBinding.slideId,
    })
  }
  return {
    ownership: isMine ? SLIDE_OWNERSHIP.MINE : SLIDE_OWNERSHIP.OTHER,
    commonSlideId,
    readable: true,
  }
}

// ---------------------------------------------------------------------------------------------
//
// Web answers from the per-slide localStorage bridge (above); desktop from the host slide's tag.
// Neither can be substituted for the other — the bridge is written in a session the desktop add-in
// never has, and the tag is unreadable in the session the web show runs in.
//
// There is no non-tag fallback behind the desktop arm: auto-start itself now requires slide-tag
// support (featureToggles/canUseAddinPowerpointAutoStart), so a desktop frame that reaches here
// can always be asked.
//
// `knownCommonSlideId` lets a caller that has ALREADY read the shown slide say so. Only the poll
// can — it reads the selection on every tick — and on web that saves a second round trip to an
// Office call that answers one frame per document. Desktop ignores it: its tag batch needs the
// slide INDEX, which comes from the same read, so re-reading is not optional there.
//
// Resolves { ownership, commonSlideId, readable } — the verdict, the slide it is about, and
// whether that verdict is SETTLED. commonSlideId is not decoration: because this is
// single-flighted, a caller can be handed a promise it did not start, about a slide that is no
// longer the one on screen. Only the caller knows which slide it asked about, so only the caller
// can tell — and it can only do that if the answer says what it is an answer TO. commonSlideId is
// null when the slide could not be identified at all, in which case ownership is UNKNOWN and there
// is nothing to misattribute.
//
// `readable` only ever qualifies an UNKNOWN, and only the SLIDE-scoped one: true means the store
// for this platform ANSWERED and the shown slide simply is not in it — no tag on desktop, no
// bridge entry on web — so the poll may memoise it. MINE/OTHER are settled by definition and the
// poll memoises them regardless. A FRAME-scoped UNKNOWN (this frame has no binding of its own yet,
// the shown slide could not be identified, the read failed) is readable=false on both platforms:
// it says nothing about that slide, so memoising it would write the slide off over an unrelated
// fact. See each branch for why.
export const resolveActiveSlideOwnership = (knownCommonSlideId = null) => {
  if (!isDesktopOfficeHost()) {
    // Deliberately outside the single-flight below. That guard exists because two concurrent
    // PowerPoint.run batches in one frame deadlock each other's context.sync() (AHA-46375); the
    // web path opens no batch at all, so serialising it would only delay a localStorage lookup.
    return resolveWebActiveSlideOwnership(knownCommonSlideId)
  }
  // The cached fast path (see ownCommonSlideId). Desktop only, because it is the desktop arm's
  // PowerPoint.run batch it exists to skip — the web arm answers from a localStorage lookup and has
  // nothing to save.
  //
  // Requires a caller that has ALREADY read the selection, which is only ever the poll: going and
  // reading it here would reinstate the round trip this is here to remove, and it is that second
  // read — issued up to ACTIVE_SLIDE_BINDING_TIMEOUT_MS after the tick's own — that can disagree
  // with the id the tick is deciding about.
  //
  // Outside the single-flight for the same reason the web arm is: that guard exists to stop two
  // PowerPoint.run batches deadlocking (AHA-46375), and this path opens none. Serialising it behind
  // an in-flight confirm would hand back a verdict about whichever slide THAT confirm started on.
  if (knownCommonSlideId !== null && ownCommonSlideId !== null
    && String(knownCommonSlideId) === String(ownCommonSlideId)) {
    log('ownership: shown slide is this frame\'s own, from cache -> mine', SHOWN_ID_LABEL,
      knownCommonSlideId)
    return Promise.resolve({
      ownership: SLIDE_OWNERSHIP.MINE,
      commonSlideId: knownCommonSlideId,
      readable: true,
    })
  }
  // Single-flight: read view can be re-entered several times in quick succession (ResizeObserver),
  // and two concurrent PowerPoint.run batches in one frame deadlock each other's context.sync()
  // (AHA-46375). Cleared on settle, so the next caller re-asks rather than reusing a verdict about
  // a slide that has since changed.
  if (activeSlideConfirmInFlight) return activeSlideConfirmInFlight
  activeSlideConfirmInFlight = runDesktopOwnershipConfirm()
    .finally(() => { activeSlideConfirmInFlight = null })
  return activeSlideConfirmInFlight
}

// Read-view entry: start unless the shown slide PROVABLY belongs to another frame. UNKNOWN starts,
// so every host that cannot answer behaves exactly as it does today.
//
// No slide check here, unlike the poll: this runs once, at the moment the view changed, and its
// question is "should I start at all" rather than "is THAT slide mine". A verdict about the slide
// shown a moment earlier is still the right basis for that.
export const isActiveSlideBoundToThisFrame = () => resolveActiveSlideOwnership()
  .then(({ ownership }) => ownership !== SLIDE_OWNERSHIP.OTHER)

// AHA-43274: how long the frame that WAS presenting waits, after the show moves off its slide,
// before concluding that no add-in frame owns what is now on screen.
//
// The reference budget for an owning frame to publish its claim — NOT the poll interval, which is
// the thing that first suggests itself and is much too short:
//
//   owner's tick phase (two unsynchronised 250ms polls)   <=  250ms
// + owner's confirm (ACTIVE_SLIDE_BINDING_TIMEOUT_MS)     <= 1200ms
//   ----------------------------------------------------------------
//   worst case before a legitimate claim exists              1450ms
//
// DELIBERATELY SHORTER THAN THAT, which is the one thing to understand before changing it. A frame
// whose confirm runs long will not have published by the time this fires, so its handover is
// indistinguishable from a plain slide and the quiz stops under it. That is not a hypothetical
// margin: 1200ms is the confirm's own timeout, so any host slow enough to approach it loses. The
// trade is deliberate — the full budget is dominated by a timeout that is almost never approached in
// practice, and waiting it out left a visible pause on the common case, which is moving onto an
// ordinary PowerPoint slide with no frame to hand over to at all.
//
// The two failure directions are unequal, so if this needs revisiting, revisit it upward. Too short
// ends a quiz another frame is legitimately presenting; too long leaves a quiz up for an extra
// moment on a slide nobody is interacting with. One constant rather than a per-platform pair: web
// could use ~375ms (its confirm is a synchronous localStorage read), but a second number would have
// to be kept in step with a timeout it does not own, for a fraction of a second nobody is timing.
const NO_OWNER_STOP_DELAY_MS = 1000

const cancelPendingStop = () => {
  if (pendingStopTimer === null) return
  clearTimeout(pendingStopTimer)
  pendingStopTimer = null
}

// Ends the quiz unless, by the time the delay is up, the last frame to declare itself owner of the
// show is SOMEBODY ELSE.
//
// WHO wrote the claim, not when, and not which slide it names. Both of those are tempting and both
// are wrong:
//
//   Matching the SLIDE ID against the slide we armed for misses a show that passes OVER an unowned
//   slide on its way to another AhaSlides slide, which two forward presses inside 1500ms does. The
//   claim then names a LATER slide than the one we armed for, the id comparison finds no match, and
//   this frame ends the quiz the incoming frame has just started.
//
//   Comparing the claim's AGE against the moment we armed misses the reverse: the frames' polls are
//   unsynchronised, so the incoming frame can resolve MINE and publish just BEFORE our own tick
//   notices we lost the slide. Widening the window by a poll interval does not rescue it — the gap
//   between our own publish and our own arm is not reliably a poll interval either, because
//   setInterval does not reschedule after an overrunning tick, so a confirm that outlasts its period
//   is followed almost immediately by the tick that arms. A window wide enough for the incoming
//   frame swallows our own claim for the slide we just left, and then the quiz never stops on a plain
//   slide — the failure this feature exists to fix.
//
// The token has neither problem, and it is exact rather than probabilistic:
//
//   Arming REQUIRES a MINE -> not-MINE edge in this frame, and the MINE that opened it published our
//   token. So at fire time the stored claim is either still ours — nobody has claimed the show since
//   we stood down, so there is nothing to hand over to and the show is on a slide no frame owns — or
//   it belongs to another frame, which can only mean that frame resolved MINE for a slide shown after
//   ours. There is no third case, and no timing assumption anywhere in it.
//
// Armed at most once per transition (the caller consumes the edge), and re-arming cancels first so
// two overlapping navigations cannot leave two timers racing to stop the same quiz.
//
// The claim is checked TWICE, before arming and again on firing, for the two orders the handover can
// arrive in. The frames' polls are unsynchronised, so the incoming frame's MINE can settle either
// side of our own not-MINE tick:
//
//   it publishes BEFORE we notice we lost the slide  -> caught here, and we never arm at all
//   it publishes DURING the debounce                 -> caught on firing, and the stop is vetoed
//
// The second check alone is correct — the first cannot see a claim the second would miss — so this
// is about not arming a timer whose only possible outcome is already known. That matters more than
// it sounds: while a timer is pending it is one more thing that has to be cancelled on every path
// out (teardown, re-assert), and the shorter that window is the fewer ways a stop can escape.
const armStopIfStillUnowned = (selectedId, onNoAhaSlideOwner) => {
  cancelPendingStop()
  const claimAtArm = loadShowOwnerClaim()
  if (claimAtArm && !claimAtArm.byThisFrame) {
    log('stop not armed: another frame already owns the show | it claims',
      claimAtArm.commonSlideId, '| we stood down from', selectedId)
    return
  }
  log('stop armed | shown ppt slide id =', selectedId, '| in', NO_OWNER_STOP_DELAY_MS, 'ms')
  pendingStopTimer = setTimeout(() => {
    pendingStopTimer = null
    const claim = loadShowOwnerClaim()
    if (claim && !claim.byThisFrame) {
      log('stop vetoed: another frame owns the show now | it claims', claim.commonSlideId,
        '| we stood down from', selectedId)
      return
    }
    log('stop firing: no other frame has claimed the show', selectedId, '| claim =', claim)
    if (onNoAhaSlideOwner) onNoAhaSlideOwner()
  }, NO_OWNER_STOP_DELAY_MS)
}

// The tag-based focus poll, replacing the legacy pollSelectedSlide for frames that have slide
// tags. Same job — notice when the shown slide becomes (or stops being) this frame's, so the
// presenter can navigate back and forth and each slide re-asserts itself — but it asks the slide
// what it is bound to instead of trusting an id this frame captured from a document-global
// selection it had no right to claim.
//
// The 250ms tick stays the cheap Common-API read. Only a CHANGE of shown slide escalates to a tag
// batch: a Rich API batch every quarter second for the length of a slideshow is not affordable,
// and two of them in one frame deadlock each other's context.sync() (AHA-46375).
//
// Acts on one poll verdict, but only if the verdict is about the slide this tick actually read.
//
// It can fail to be. resolveActiveSlideOwnership is single-flighted, so a tick that arrives while
// an earlier confirm is still running — the read-view entry confirm, or a previous tick's — is
// handed THAT confirm's promise, which answers for whichever slide was on screen when it started.
// If the presenter has since navigated, acting on it would have this frame re-assert itself over
// somebody else's slide, and recording it would memoise the new slide as resolved so the poll
// never re-asks and never corrects. That is the oscillation this whole module exists to stop,
// reached through the back door.
//
// Dropping the verdict costs one tick (250ms): the memo is left untouched, so the next tick asks
// again — by which time the earlier confirm has settled and cleared the single-flight slot.
const applyPollOwnership = ({ ownership, commonSlideId, readable }, selectedId, handlers) => {
  const { onMine, onOther, onNoAhaSlideOwner } = handlers
  if (commonSlideId !== null && String(commonSlideId) !== String(selectedId)) {
    log('tag poll: verdict is about slide', commonSlideId, 'but', selectedId,
      'is shown -> discarding, will re-ask next tick')
    return
  }
  log('tag poll: shown =', selectedId, '->', ownership)
  if (ownership === SLIDE_OWNERSHIP.MINE) {
    tagPollResolvedSlideId = selectedId
    wasMineLastVerdict = true
    // AHA-43274: a re-asserted MINE cancels a stop this frame had already armed, which is what
    // makes the delay a DEBOUNCE rather than just a delay. A tick that could not read the tag
    // leaves the edge unconsumed (see below), so the sequence "unreadable -> MINE" must not end
    // the quiz — and without this cancel the armed timer would fire anyway.
    cancelPendingStop()
    // Stamp this frame as owner of the show. This is what lets a frame that later stands down
    // distinguish a handover from a plain slide, and it is also what makes the stop's veto exact:
    // every arming is preceded by a publish from the frame that arms, so a claim that is NOT ours at
    // fire time can only have come from a frame that owned a slide shown after ours. See
    // armStopIfStillUnowned.
    publishShowOwnerClaim(selectedId)
    // autoStartActiveSlideFromView no-ops while this slide is already the presenting one, so
    // re-asserting is only visible when focus actually moved away and came back.
    if (onMine) onMine()
    return
  }
  // Any SETTLED not-MINE verdict, which is OTHER (settled by definition — the shown slide was read
  // and named a real AhaSlides binding that is not this frame's) or UNKNOWN with readable=true (the
  // store ANSWERED and the slide simply is not in it).
  //
  // The two are treated IDENTICALLY on purpose, and that is a change from the first cut of this
  // feature. Which of them it is answers "is the shown slide in the list of AhaSlides slides", but
  // that list is stored state and stored state can lie: web bridge entries are never cleaned up, so
  // deleting an add-in from a slide leaves its entry behind and the slide keeps resolving to OTHER
  // for the rest of the deck's life. Deciding from a LIVE claim instead cannot be fooled that way —
  // an absent claim is the only signal that contradicts a stale store.
  //
  // A memo is still taken for both, because both are settled facts about that slide. That is what
  // keeps the "escalate only on a CHANGE of shown slide" rule below true for the case that
  // dominates a real deck: without it, parking on a plain slide would re-issue a PowerPoint.run tag
  // batch on every 250ms tick for the length of the show, in every add-in frame — the AHA-46375
  // shape this module's own comment rules out.
  const settled = ownership === SLIDE_OWNERSHIP.OTHER || readable
  if (!settled) {
    // UNKNOWN that could not tell: an unreadable settings read, a rejected or timed-out batch, or a
    // frame with no binding of its own (see resolveActiveSlideOwnership — that one is frame-scoped,
    // not slide-scoped). No memo, so it is re-asked next tick, exactly as before. Critically it
    // also does NOT consume wasMineLastVerdict: it says nothing about whether this frame is still
    // the owner, and consuming it would silently disarm the one frame allowed to end the quiz.
    return
  }
  tagPollResolvedSlideId = selectedId
  if (wasMineLastVerdict) {
    wasMineLastVerdict = false
    armStopIfStillUnowned(selectedId, onNoAhaSlideOwner)
  }
  if (onOther) {
    // Arm the re-start flag: presentation.activeSlide may still point at this frame's slide, and
    // without this the return to it would look like "already presenting" and never re-assert.
    //
    // EVERY settled not-MINE verdict, which is a deliberate widening from "OTHER only" and is a
    // RESTORATION rather than a new behaviour. The legacy pollSelectedSlide armed this flag on
    // `selectedId !== savedHostPptSlideId` (AHA-43226) — any shown slide that was not the one this
    // frame had captured, plain PowerPoint slides included. AHA-46751 deleted that poll for good
    // reason (the captured id was a document-global value every frame falsely claimed) and re-homed
    // the flag onto the OTHER branch alone, which silently dropped the plain-slide half of its
    // coverage. This puts it back.
    //
    // What that gap costs, which only became visible once AHA-43274 gave a plain slide something to
    // recover FROM: this frame's own stop resets its quiz but deliberately leaves
    // presentation.presenting true and never moves presentation.activeSlide off this slide. So on
    // the way back both halves of autoStartActiveSlideFromView's `alreadyPresentingThisSlide` still
    // hold, and with the flag unset the re-assert early-returns — leaving the quiz parked in its
    // lobby for the rest of the show.
    //
    // NOT gated on the AHA-43274 flag, and that is the point of restoring it here rather than
    // adding a second handler beside it. Arming the flag is the June behaviour this is putting back,
    // not part of the new feature; only the new USE of it — the send guard in startActiveSlidePPT —
    // is gated. Fixing the regression and shipping the feature stay separable.
    onOther()
  }
}

// A SETTLED UNKNOWN — the shown slide was read and carries no tag — is recorded as resolved, so
// the poll stops re-asking it. A transient UNKNOWN is not, so a slide that could not be read is
// re-asked on the next tick rather than written off for the rest of the show. See
// applyPollOwnership for the split.
export const pollSelectedSlideBasedOnTag = (handlers = {}) => {
  if (tagPollBusy) return
  // Claimed BEFORE the read, not inside its callback. getSelectedDataAsync can outlast the 250ms
  // tick — readSelectedSlideIdBounded budgets 2500ms for exactly that case — and a flag set in the
  // callback leaves every tick in between free to issue a read of its own. Their callbacks then
  // all reach the confirm, all get handed the same single-flighted promise, and all act on it:
  // onOther/onMine fire several times for one verdict.
  //
  // The cost of claiming early is that a read which NEVER calls back parks the poll for the rest
  // of the show. That is bounded — stopTagSlideFocusPolling clears the flag when the show ends —
  // and a host whose selection read never returns has already lost the poll either way, since the
  // old code's extra ticks would not have come back either.
  tagPollBusy = true
  readSelectedSlideId((selectedId, error) => {
    if (error) {
      log('tag poll error:', error)
      tagPollBusy = false
      return
    }
    if (!selectedId || selectedId === tagPollResolvedSlideId) {
      tagPollBusy = false
      return
    }
    // The tick's own selection read is handed on: on web it IS the answer the confirm would
    // otherwise have to go and fetch, and fetching it again risks the starved-frame hang.
    resolveActiveSlideOwnership(selectedId)
      .then((verdict) => {
        applyPollOwnership(verdict, selectedId, handlers)
        return null
      })
      // finally before catch: the busy flag must clear on every outcome, and eslint's
      // promise/catch-or-return wants the chain to END in a catch.
      .finally(() => { tagPollBusy = false })
      .catch((confirmError) => log('tag poll confirm failed:', confirmError))
  })
}

// `handlers` are how this module talks back WITHOUT importing the store. It is deliberately a
// leaf: @/vuex/store's module graph reaches back into services/office, so importing it here would
// close an import cycle through the very module that drives this poll. The outcomes — onMine,
// onOther (the shown slide is settled and is not this frame's, whether another frame owns it or
// nobody does), and onNoAhaSlideOwner (this frame was presenting, the show has settled on a slide no
// add-in frame claims) — are the caller's to act on anyway; this module's job ends at deciding whose
// slide is on screen. In particular it never learns what a quiz is, and never sees a feature flag:
// both tests belong to the caller, where the store already is.
//
// onOther and onNoAhaSlideOwner overlap on the plain-slide case and report it at different times,
// which is the point. onOther fires on the verdict itself, for any frame; onNoAhaSlideOwner only
// after the stop debounce has run and survived its veto, and only for the frame that was presenting.
export const startTagSlideFocusPolling = (handlers = {}) => {
  if (tagSlidePollTimer !== null) return // already polling for this instance
  log('start tag poll')
  tagSlidePollTimer = setInterval(() => pollSelectedSlideBasedOnTag(handlers), SLIDE_POLL_INTERVAL_MS)
}

// Also resets the resolved-slide memo: a poll restarted for a new show must not inherit a verdict
// about the slide the previous one ended on, or it would skip the very first tick that matters.
//
// AHA-43274 adds two more pieces of per-show state to drop, for the same reason. A timer left armed
// would fire after the show has ended and stop a presentation the presenter may have gone back to
// running from the editor; a sticky wasMineLastVerdict would let the NEXT show's first not-MINE tick
// end a quiz this frame never started in it.
//
// The show-owner claim is deliberately NOT cleared here, and that is a correctness point rather than
// laziness. The claim is ONE DOCUMENT-SCOPED KEY SHARED BY EVERY FRAME, and this function does not
// only run when the show ends: commitView calls it on any committed 'edit', which per the comment
// there fires for a single frame while the show is still running — clicking back into the editing
// window does not close the Slide Show. A frame clearing on its way out would therefore delete the
// claim of whichever frame is still presenting, and any frame with a stop already armed would then
// read "nobody owns the show" and end a live quiz. Nothing this frame knows can tell whose claim is
// in there, so the safe move is to touch it only when publishing its own.
//
// Nothing is lost by leaving it: armStopIfStillUnowned vetoes on a claim NEWER than the moment it
// armed, so a claim left behind by an earlier show can never vouch for this one, and
// CLAIM_MAX_AGE_MS still prunes it on read.
export const stopTagSlideFocusPolling = () => {
  if (tagSlidePollTimer === null) return
  clearInterval(tagSlidePollTimer)
  tagSlidePollTimer = null
  tagPollResolvedSlideId = null
  tagPollBusy = false
  cancelPendingStop()
  wasMineLastVerdict = false
  // The own-slide cache goes too. It is not per-show state the way the two above are — the id is a
  // property of the frame, and it does not change while the frame lives — but the DECK is editable
  // between shows, and a frame whose slide was deleted and replaced would otherwise keep answering
  // MINE for an id that now belongs to something else. Re-learning costs one tag read on the first
  // tick of the next show, against a fast path that is wrong for the whole of it.
  ownCommonSlideId = null
  log('stop tag poll')
}
